import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, DataTable, Select, Tooltip, Typography, useSelect } from '@chilecompra/react-kit/components'
import { REACT_APP_SUPPLIER_PROFILE_BASE_URL } from '../../config/settings/environment'
import { formatAmount } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import useBubbleChart from '../../components/Charts/BubbleChart.hooks'
import BubbleChart from '../../components/Charts/BubbleChart'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import SuppliersAmountNoInfo from '../../components/Errors/SuppliersAmountNoInfo'
import FichaSuppliersAmount from '../../components/Infographics/FichaSuppliersAmount'
import routes from '../../config/settings/routes'
import {
  onSuppliersAmountThunk,
  onSuppliersAmountDetailThunk,
  onSuppliersAmountDetailUnload
} from './SuppliersAmount.actions'
import { Wrapper, GridWrapper, ProviderButton, Link, InlineLabel, Info } from './SuppliersAmount.styles'

/**
 * Thee SuppliersAmount's container.
 */
const SuppliersAmount = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { latestUpdate } = useSelector(state => state.topNavbar)
  const { idEntity, name } = useSelector(state => state.buyerSection)
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const inputBubbleChartRef = useRef()
  const dataTableRef = useRef()
  const [sort, setSort] = useState(0)
  const [showChart, setShowChart] = useState(false)
  const [supplier, setSupplier] = useState({})
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Montos_Transados_Proveedores_${years[0].value}`)
  const {
    detail,
    data: { chartStructure, csvStructure: providersCsvStructure },
    fetching: loading
  } = useSelector(state => state.suppliersAmounts)
  const csvStructure = useMemo(
    () =>
      showChart
        ? [
            [
              'Nombre proveedor',
              'RUT proveeder',
              'Monto total transado (CLP)',
              'Órdenes de compra asociadas',
              'Licitaciones adjudicadas',
              'Ficha proveedor'
            ],
            [
              supplier.provider,
              supplier.rutProvider,
              supplier.annualCLPGrossAmount,
              supplier.annualPOnumber,
              supplier.annualLCnumber,
              `${REACT_APP_SUPPLIER_PROFILE_BASE_URL}/${supplier.rutProvider}`
            ]
          ]
        : providersCsvStructure,
    [providersCsvStructure, showChart, supplier]
  )
  const { value: yearsValue, onChange: handleChange } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(onSuppliersAmountThunk(idEntity, updatedValue))
      setFileName('Montos_Transados_Proveedores_' + updatedValue)
    }
  })
  const handleChart = id => () => {
    const singleSup = chartStructure.find(item => item.rutProvider === id)

    dispatch(onSuppliersAmountDetailThunk(idEntity, yearsValue, singleSup.rutProvider))
    setShowChart(true)
    setSupplier(singleSup)
  }
  const { serie: structure, months: categories, ranges: values } = useBubbleChart(detail)
  const handleReturn = () => {
    dispatch(onSuppliersAmountDetailUnload())
    setShowChart(false)
  }
  const handleOpenProfile = () => navigate(`${routes.provider.replace(/:dni/, supplier.rutProvider)}`)
  const handleClickSort = (_, updatedValue) => setSort(updatedValue)

  const handleCaptureImage = () => exportToImage('SuppliersAmount', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    dispatch(onSuppliersAmountThunk(idEntity, yearsValue))
  }, [initialLoading, idEntity])

  return (
    <Wrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Montos transados con cada proveedor
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            A continuación podrás conocer los montos que ha transado {name} con todos y cada uno de los proveedores
            según el año seleccionado, y al hacer click sobre el proveedor podrás revisar gráficamente el detalle de la
            información. Recuerda que estos montos que se extraen de la orden de compra que emite una entidad en{' '}
            <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a compromisos de
            los organismos, no a pagos.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
            impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      {!showChart ? (
        <GridWrapper container justifyContent="center">
          <GridWrapper container item md={8} xs={12}>
            <GridWrapper item md={3} xs={12}>
              <Select label="AÑO" options={years} value={yearsValue} onChange={handleChange} />
            </GridWrapper>
            <GridWrapper item md={5} xs={12} />
          </GridWrapper>
          <GridWrapper item md={2} xs={12} />
        </GridWrapper>
      ) : null}
      <GridWrapper container justifyContent="center">
        {showChart ? (
          <GridWrapper item xs={12} md={10} margin="48px 0 0 0">
            <GridWrapper container justifyContent="space-between">
              <GridWrapper item xs={12} md={6}>
                <IconButton type="back" onClick={handleReturn} fontWeight="bold" fontSize="16px">
                  <InlineLabel>Volver a los proveedores del año {yearsValue}</InlineLabel>
                </IconButton>
              </GridWrapper>
            </GridWrapper>
            <GridWrapper container justifyContent="center">
              <GridWrapper item xs={12} md={9} margin="40px 0 0 0">
                <BubbleChart
                  categories={categories}
                  fileName={fileName}
                  id="SuppliersAmount"
                  inputBubbleChartRef={inputBubbleChartRef}
                  structure={structure}
                  values={values}
                />
              </GridWrapper>
              <GridWrapper item xs={12} md={3} margin="45px 0 0 0" padding="0 15px">
                <Typography variant="h2" fontSize="16px" lineHeight="22px" fontWeight="bold">
                  {supplier.provider}
                </Typography>
                <Typography variant="body2" fontSize="14px" margin="20px 0 0">
                  RUT
                </Typography>
                <Typography variant="h3" fontSize="18px" fontWeight="bold">
                  {supplier.rutProvider}
                </Typography>
                <Typography variant="body2" fontSize="14px" margin="20px 0 0">
                  Monto total transado
                </Typography>
                <Typography variant="h3" fontSize="18px" fontWeight="bold">
                  ${formatAmount(supplier.annualCLPGrossAmount)}
                </Typography>
                <Typography variant="body2" fontSize="14px" margin="20px 0 0">
                  Órdenes de compra asociadas
                  <Tooltip
                    content={
                      <Typography color="white" fontSize="12px" lineHeight="16px">
                        Las órdenes de compra provienen de todos los mecanismos de compra utilizados, convenio marco,
                        trato directo o licitaciones.
                      </Typography>
                    }
                    placement="top"
                  >
                    <Info />
                  </Tooltip>
                </Typography>
                <Typography variant="h3" fontSize="18px" fontWeight="bold">
                  {formatAmount(supplier.annualPOnumber)}
                </Typography>
                <Typography variant="body2" fontSize="14px" margin="20px 0 0">
                  Licitaciones adjudicadas
                </Typography>
                <Typography variant="h3" fontSize="18px" fontWeight="bold">
                  {formatAmount(supplier.annualLCnumber)}
                </Typography>
                <Button
                  color="primary"
                  margin="24px 0 0"
                  padding="0"
                  variant="contained"
                  onClick={handleOpenProfile}
                  width="100%"
                >
                  Ver informe proveedor
                </Button>
              </GridWrapper>
            </GridWrapper>
          </GridWrapper>
        ) : chartStructure.length >= 1 ? (
          <GridWrapper item xs={12} md={10}>
            <DataTable
              enableResults
              headers={[
                { label: 'Nombre proveedor', value: 0 },
                { label: 'RUT proveedor', value: 1 },
                { label: 'Monto transado', value: 2 },
                { label: 'N° de órdenes de compra', value: 3 }
              ]}
              headersCellsProps={[
                { enableFilter: true, enableSort: true, initialSort: sort === 0 },
                { enableFilter: true, enableSort: true, initialSort: sort === 1, width: '15%' },
                { enableSort: true, initialSort: sort === 2, width: '15%' },
                { enableSort: true, initialSort: sort === 3, width: '20%' }
              ]}
              initialRows={10}
              loading={loading}
              records={csvStructure.slice(1)}
              recordsDecorators={[
                ([name, dni]) => <ProviderButton onClick={handleChart(dni)}>{name}</ProviderButton>,
                ([, dni]) => dni,
                ([, , amount]) => `$${formatAmount(amount)}`,
                ([, , , totalOCs]) => totalOCs
              ]}
              ref={dataTableRef}
              onSortColumn={handleClickSort}
            />
          </GridWrapper>
        ) : (
          <GridWrapper item xs={12} md={10}>
            <SuppliersAmountNoInfo />
          </GridWrapper>
        )}
        <GridWrapper item xs={12} md={10} margin="32px 0 0">
          <Infobar
            disableDownloadChart={!showChart}
            disableInfographic={!showChart}
            expandArrow={expandArrow}
            infographic={<FichaSuppliersAmount />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

export default SuppliersAmount
